/**
 * Veroni required-option guard.
 *
 * The bug this fixes: on any ring page, clicking "Add to cart" without picking
 * a ring size did nothing at all — no message, no scroll, no cart change.
 *
 * Why. The ring size is a required <select> associated with the buy-buttons
 * form via the `form=` attribute (blocks/veroni-ring-size.liquid). Horizon's
 * AddToCartComponent.handleClick (assets/product-form.js) gates on
 * `form.checkValidity()`, which reports validity WITHOUT showing anything, and
 * then returns. The browser's own constraint UI does not rescue it either: the
 * select sits thousands of pixels below the sticky buy box, and Chrome focuses
 * it without scrolling, so the validation bubble renders off-screen. Net effect
 * for a shopper: the button is dead. Confirmed live on 2026-08-07.
 *
 * The fix: intercept the click in the CAPTURE phase on document. Horizon binds
 * every `on:click` through one delegated listener in the BUBBLE phase
 * (assets/component.js), so stopping propagation here runs before both the main
 * add-to-cart handler and the sticky bar's — which also means the sticky bar no
 * longer plays its fly-to-cart animation for an add that never happened.
 *
 * Deliberately generic: it validates whatever the form says is required, so any
 * future required line-item property (engraving, chain length) is covered
 * without touching this file.
 */

/** Marker so we never double-insert an error node. */
const ERROR_CLASS = 'v-field-error';

/** @type {WeakMap<HTMLElement, () => void>} field -> listener teardown */
const watched = new WeakMap();

/**
 * Is this click one that is trying to add to cart?
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null} the triggering button, or null
 */
function addToCartTrigger(target) {
  if (!(target instanceof Element)) return null;
  const button = target.closest('button');
  if (!button || button.disabled) return null;
  if (button.closest('add-to-cart-component')) return button;
  // The sticky bar proxies its click onto the real button, but it also runs its
  // own animation, so it has to be gated here too. The element is
  // <sticky-add-to-cart> (see assets/sticky-add-to-cart.js), not
  // sticky-add-to-cart-component.
  if (button.closest('sticky-add-to-cart')) return button;
  return null;
}

/**
 * @param {HTMLElement} button
 * @returns {HTMLFormElement | null}
 */
function resolveForm(button) {
  const own = button.closest('form[data-type="add-to-cart-form"]');
  if (own instanceof HTMLFormElement) return own;

  // Sticky bar: it lives outside the form, so fall back to the product form in
  // the main product section — the same one the sticky bar itself puppets.
  const section = button.closest('.shopify-section') ?? document;
  const scoped = section.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  if (scoped instanceof HTMLFormElement) return scoped;

  const global = document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  return global instanceof HTMLFormElement ? global : null;
}

/**
 * The element to hang the message off: the field's labelled wrapper if it has
 * one, otherwise the field itself.
 * @param {HTMLElement} field
 */
function anchorFor(field) {
  return field.closest('.v-ring-size, .product-custom-property, .v-field') ?? field;
}

/**
 * @param {HTMLElement & {validationMessage?: string}} field
 */
function messageFor(field) {
  return field.dataset.veroniRequiredMessage || field.validationMessage || 'Please complete this field.';
}

/**
 * Shows an inline, screen-reader-announced error under the offending field and
 * clears it as soon as the shopper fixes it.
 * @param {HTMLElement & {validationMessage?: string, checkValidity?: () => boolean}} field
 */
function flag(field) {
  const anchor = anchorFor(field);
  anchor.setAttribute('data-veroni-invalid', '');

  let node = anchor.nextElementSibling;
  if (!(node instanceof HTMLElement) || !node.classList.contains(ERROR_CLASS)) {
    node = document.createElement('p');
    node.className = ERROR_CLASS;
    node.setAttribute('role', 'alert');
    anchor.after(node);
  }
  node.textContent = messageFor(field);

  if (!node.id) node.id = `v-field-error-${Math.random().toString(36).slice(2, 9)}`;
  field.setAttribute('aria-describedby', node.id);
  field.setAttribute('aria-invalid', 'true');

  if (!watched.has(field)) {
    const clear = () => {
      if (field.checkValidity?.() === false) return;
      anchor.removeAttribute('data-veroni-invalid');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      node?.remove();
      field.removeEventListener('change', clear);
      field.removeEventListener('input', clear);
      watched.delete(field);
    };
    field.addEventListener('change', clear);
    field.addEventListener('input', clear);
    watched.set(field, clear);
  }
}

/**
 * @param {HTMLElement} field
 */
function reveal(field) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  field.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  // Focus after the scroll is queued; focusing first would make Chrome jump.
  requestAnimationFrame(() => {
    try {
      field.focus({ preventScroll: true });
    } catch {
      field.focus();
    }
  });
}

document.addEventListener(
  'click',
  (event) => {
    const button = addToCartTrigger(event.target);
    if (!button) return;

    const form = resolveForm(button);
    if (!form || form.checkValidity()) return;

    // Nothing downstream should treat this as an add: not the native submit,
    // not Horizon's delegated handler, not the sticky bar's animation.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const invalid = /** @type {HTMLElement[]} */ (Array.from(form.elements)).filter(
      (el) => /** @type {any} */ (el).willValidate && !(/** @type {any} */ (el).checkValidity())
    );

    invalid.forEach(flag);
    if (invalid[0]) reveal(invalid[0]);
  },
  true
);
